import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useSelector } from "react-redux";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams
} from "react-router-dom";

import { useGetPartnerDetailAPI } from "@/api/partners/usePartnersAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import GlobalModal from "@/components/ui/globalModal";
import Toast from "@/components/ui/toast";
import { ModalProvider } from "@/context/ModalProvider";
import { useAuth } from "@/hooks/auth/useAuth";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import ActivateUserPage from "@/pages/activateUser";
import AnalysisHistoryPage from "@/pages/analysisHistory";
import DatasetManagementPage from "@/pages/datasetManagement";
import DatasetManagementDetailPage from "@/pages/datasetManagement/detail";
import ErrorBoundaryPage from "@/pages/errorBoundary";
import GroupManagementPage from "@/pages/groupManagement";
import HomePage from "@/pages/home";
import LoginPage from "@/pages/login";
import NotFoundPage from "@/pages/notFound";
import ResetPasswordPage from "@/pages/resetPassword";
import SsoCallbackPage from "@/pages/sso/callback";
import UserManagementPage from "@/pages/userManagement";
import { RootState } from "@/store";
import { ESidebarMenuType } from "@/store/slice/uiSlice";
import { ErrorBoundaryType } from "@/types/errors";
import { getSubdomain } from "@/utils/location";

import FeatureFlagRoute from "./FeatureFlagRoute";
import ProtectedRoute from "./protectedRoute";

const APP_TITLE_SUFFIX = " - LOKASI";

const AppRouter = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;
  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get("preview") === "true";
  const { isAnalysisHistoryEnabled } = useAppFeatures();
  const userPermissions = useGetUserPermission({ enabled: isAuthenticated });
  const isUserManagementGranted =
    userPermissions[EModuleName.USER_MANAGEMENT][EModulePermission.VIEW];
  const isUserGroupManagementGranted =
    userPermissions[EModuleName.USER_GROUP_MANAGEMENT][EModulePermission.VIEW];
  const isDatasetManagementGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.VIEW];
  const { isLoading: isUserProfileLoading } = useGetUserProfileAPI({
    enabled: isAuthenticated
  });

  const {
    sideBarMenuTypePopupDisplay,
    showProjectList,
    showDatasetExplorerPopup,
    showSearchLocationPopup
  } = useSelector((state: RootState) => state.ui);

  const { data: partnerData } = useGetPartnerDetailAPI(getSubdomain());

  const fallBackRender = ({ error, resetErrorBoundary }: ErrorBoundaryType) => (
    <ErrorBoundaryPage
      errorMessage={error.message}
      onReset={resetErrorBoundary}
    />
  );

  // Effect to update the favicon with the partner's app icon URL if exists
  useEffect(() => {
    if (typeof document !== "undefined" && partnerData?.app_icon_url) {
      // Find existing favicon or create a new one
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      // If no link element exists, create one
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }

      // Set the new favicon URL
      link.href = partnerData.app_icon_url;
    }
  }, [partnerData?.app_icon_url]);

  // Effect to update document title
  useEffect(() => {
    if (typeof document !== "undefined") {
      const getDocumentTitle = (): string => {
        switch (true) {
          case pathName === "/":
            if (
              sideBarMenuTypePopupDisplay === ESidebarMenuType.SPATIAL &&
              !showProjectList &&
              !isPreviewMode &&
              !showDatasetExplorerPopup
            ) {
              return `Spatial Analysis${APP_TITLE_SUFFIX}`;
            }
            if (showProjectList) {
              return `Project List${APP_TITLE_SUFFIX}`;
            }
            if (showDatasetExplorerPopup) {
              return `Data Explorer${APP_TITLE_SUFFIX}`;
            }
            if (showSearchLocationPopup) {
              return `Search${APP_TITLE_SUFFIX}`;
            }
            if (isPreviewMode) {
              return `Dataset Management${APP_TITLE_SUFFIX}`;
            }
            return `Spatial Analysis${APP_TITLE_SUFFIX}`;
          case pathName === "/user-management":
            return `User Management${APP_TITLE_SUFFIX}`;
          case pathName === "/group-management":
            return `Group Management${APP_TITLE_SUFFIX}`;
          case pathName.includes("/dataset-management"):
            return `Dataset Management${APP_TITLE_SUFFIX}`;
          case pathName === "/draw":
            return `Draw Polygon${APP_TITLE_SUFFIX}`;
          case pathName === "/login":
            return `Login${APP_TITLE_SUFFIX}`;
          case pathName === "/reset-password":
            return `Reset Password${APP_TITLE_SUFFIX}`;
          case pathName === "/activate-user":
            return `Activate User${APP_TITLE_SUFFIX}`;
          case pathName === "/analysis-history":
            return `Analysis History${APP_TITLE_SUFFIX}`;
          default:
            return `Not Found${APP_TITLE_SUFFIX}`;
        }
      };

      // Use app_title from partner data if available, otherwise use the computed title
      document.title = partnerData?.app_title ?? getDocumentTitle();
    }
  }, [
    partnerData?.app_title,
    pathName,
    sideBarMenuTypePopupDisplay,
    showProjectList,
    showDatasetExplorerPopup,
    showSearchLocationPopup,
    isPreviewMode
  ]);

  return (
    <ModalProvider>
      <Toast />
      <GlobalModal />

      <ErrorBoundary
        fallbackRender={fallBackRender}
        onReset={() => navigate("/login", { replace: true })}
      >
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/sso/callback" element={<SsoCallbackPage />} />

          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/activate-user" element={<ActivateUserPage />} />

          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<HomePage />} />
            <Route
              path="/user-management"
              element={
                <FeatureFlagRoute
                  isFeatureEnabled={isUserManagementGranted}
                  isLoading={isUserProfileLoading}
                >
                  <UserManagementPage />
                </FeatureFlagRoute>
              }
            />
            <Route
              path="/group-management"
              element={
                <FeatureFlagRoute
                  isFeatureEnabled={isUserGroupManagementGranted}
                  isLoading={isUserProfileLoading}
                >
                  <GroupManagementPage />
                </FeatureFlagRoute>
              }
            />

            <Route
              path="/dataset-management"
              element={
                <FeatureFlagRoute
                  isFeatureEnabled={isDatasetManagementGranted}
                  isLoading={isUserProfileLoading}
                >
                  <DatasetManagementPage />
                </FeatureFlagRoute>
              }
            />
            <Route
              path="/dataset-management/:id"
              element={
                <FeatureFlagRoute
                  isFeatureEnabled={isDatasetManagementGranted}
                  isLoading={isUserProfileLoading}
                >
                  <DatasetManagementDetailPage />
                </FeatureFlagRoute>
              }
            />

            <Route
              path="/analysis-history"
              element={
                <FeatureFlagRoute
                  isFeatureEnabled={isAnalysisHistoryEnabled}
                  isLoading={isUserProfileLoading}
                >
                  <AnalysisHistoryPage />
                </FeatureFlagRoute>
              }
            />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ErrorBoundary>
    </ModalProvider>
  );
};

export default AppRouter;
